import React, { useRef, useEffect } from 'react';
import { SoccerBall, Cards, ArrowsLeftRight, Warning, FirstAid, Flag } from '@phosphor-icons/react';
import { ScrollArea } from './ui/scroll-area';

const EventIcon = ({ type }) => {
  switch (type) {
    case 'goal': return <SoccerBall size={18} weight="fill" />;
    case 'yellow_card': return <Cards size={16} weight="fill" />;
    case 'red_card': return <Cards size={16} weight="fill" />;
    case 'substitution': return <ArrowsLeftRight size={16} weight="fill" />;
    case 'save': return <Warning size={16} weight="fill" />;
    case 'injury': return <FirstAid size={16} weight="fill" />;
    case 'kickoff':
    case 'halftime':
    case 'fulltime': return <Flag size={16} weight="fill" />;
    default: return null;
  }
};

const eventTone = (type) => {
  switch (type) {
    case 'goal': return { ring: '#32D74B', glow: 'rgba(50, 215, 75, 0.45)', accent: '#32D74B', label: 'GOAL' };
    case 'yellow_card': return { ring: '#FFD60A', glow: 'rgba(255, 214, 10, 0.35)', accent: '#FFD60A', label: 'YELLOW' };
    case 'red_card': return { ring: '#FF3B30', glow: 'rgba(255, 59, 48, 0.45)', accent: '#FF3B30', label: 'RED' };
    case 'substitution': return { ring: '#4DA3FF', glow: 'rgba(77, 163, 255, 0.35)', accent: '#4DA3FF', label: 'SUB' };
    case 'save': return { ring: '#A0A0A5', glow: 'rgba(160, 160, 165, 0.25)', accent: '#A0A0A5', label: 'SAVE' };
    case 'injury': return { ring: '#FF6B00', glow: 'rgba(255, 107, 0, 0.4)', accent: '#FF6B00', label: 'INJURY' };
    default: return { ring: '#6E6E73', glow: 'rgba(110, 110, 115, 0.2)', accent: '#A0A0A5', label: 'INFO' };
  }
};

const formatEventMessage = (event) => {
  switch (event.type) {
    case 'goal': {
      const a = event.assister ? ` · assist ${event.assister.name}` : '';
      return `${event.player?.name || 'Goal'}${a}`;
    }
    case 'yellow_card': return `${event.player?.name} booked`;
    case 'red_card': return `${event.player?.name} sent off`;
    case 'substitution': return `${event.playerOut?.name} ⇄ ${event.playerIn?.name}`;
    case 'save': return `${event.player?.name || 'Keeper'} denies the chance`;
    case 'injury': return `${event.player?.name} injured (${event.injuryWeeks}w)`;
    case 'halftime':
    case 'fulltime':
    case 'kickoff': return event.message;
    case 'minute': return null;
    default: return event.message || '';
  }
};

export const MatchEvents = ({ events = [], currentMinute = 0, isLive = false }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current && isLive) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events, isLive]);

  const displayEvents = events.filter(e => formatEventMessage(e) !== null);

  return (
    <div className="ewfl-events" data-testid="match-events">
      <div className="ewfl-events-head">
        <div className="ewfl-events-title">
          <span className="ewfl-events-bar" />
          <h3>LIVE TICKER</h3>
        </div>
        {isLive && (
          <div className="ewfl-events-clock">
            <span className="ewfl-live-dot" />
            <span className="ewfl-events-minute">{currentMinute}<span className="ewfl-tick">'</span></span>
          </div>
        )}
      </div>

      <ScrollArea className="ewfl-events-scroll" ref={scrollRef}>
        <div className="ewfl-events-list">
          {displayEvents.length === 0 ? (
            <div className="ewfl-events-empty">
              <span className="ewfl-events-empty-circle" />
              Awaiting kick-off…
            </div>
          ) : (
            displayEvents.map((event, idx) => {
              const message = formatEventMessage(event);
              const tone = eventTone(event.type);
              const isLatest = idx === displayEvents.length - 1 && isLive;
              return (
                <div
                  key={`${event.type}-${idx}`}
                  data-testid={`event-${event.type}-${idx}`}
                  className={`ewfl-event-card ewfl-event-card--${event.type} ${isLatest ? 'ewfl-event-card--latest' : ''}`}
                  style={{
                    '--ring': tone.ring,
                    '--glow': tone.glow,
                    animationDelay: `${Math.max(0, displayEvents.length - 1 - idx) * 0.02}s`,
                  }}
                >
                  <div className="ewfl-event-minute">
                    <span>{event.minute !== undefined ? `${event.minute}` : '–'}</span>
                    <span className="ewfl-tick">'</span>
                  </div>
                  <div className="ewfl-event-icon" style={{ color: tone.accent }}>
                    <EventIcon type={event.type} />
                  </div>
                  <div className="ewfl-event-body">
                    <div className="ewfl-event-row">
                      <span className="ewfl-event-tag" style={{ color: tone.accent, borderColor: tone.accent }}>
                        {tone.label}
                      </span>
                      <span className="ewfl-event-msg">{message}</span>
                    </div>
                    {event.commentary && (
                      <p className="ewfl-event-commentary">{event.commentary}</p>
                    )}
                  </div>
                  {event.team && (
                    <span className={`ewfl-event-side ewfl-event-side--${event.team}`}>
                      {event.team === 'home' ? 'H' : 'A'}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
